<template>
  <div class="h-[280px] relative">
    <Doughnut :data="chartData" :options="chartOptions" />
  </div>
</template>

<script setup lang="ts">
import { computed, shallowRef, defineProps } from 'vue'
import { Doughnut } from 'vue-chartjs'
import { useAccountStore } from '@/stores/account'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'

ChartJS.register(ArcElement, Tooltip, Legend)

const accountStore = useAccountStore()

const props = defineProps({
  startDate: {
    type: Date,
    required: true
  },
  endDate: {
    type: Date,
    required: true
  }
})

const chartOptions = shallowRef({
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 500
  },
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: {
        usePointStyle: true,
        padding: 16,
        boxWidth: 6,
        boxHeight: 6,
        font: {
          size: 11
        }
      }
    },
    title: {
      display: true,
      text: 'Distribuição de Despesas por Centro de Custo',
      font: {
        size: 14,
        weight: 'bold'
      },
      padding: {
        bottom: 15
      }
    },
    tooltip: {
      callbacks: {
        label: (context: any) => {
          const label = context.label || ''
          const value = context.raw || 0
          return `${label}: ${new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
          }).format(value)}`
        }
      }
    }
  }
})

const chartColors = shallowRef([
  '#FF6384',  // Vermelho
  '#36A2EB',  // Azul
  '#FFCE56',  // Amarelo
  '#4BC0C0',  // Verde água
  '#9966FF',  // Roxo
  '#FF9F40',  // Laranja
  '#FF99CC',  // Rosa claro
  '#00CC99',  // Verde menta
  '#6699FF',  // Azul claro
  '#FF6666'   // Vermelho claro
])

const chartData = computed(() => {
  if (!accountStore.accounts?.length) {
    return {
      labels: [],
      datasets: [{
        data: [],
        backgroundColor: []
      }]
    }
  }

  // Agrupar despesas por centro de custo dentro do período selecionado
  const expensesByCostCenter = accountStore.accounts
    .filter(account => {
      const dueDate = new Date(account.dueDate)
      return account.type === 'payable' && 
        dueDate >= props.startDate && 
        dueDate <= props.endDate
    })
    .reduce((acc, account) => {
      const costCenter = account.costCenterName || 'Sem Centro de Custo'
      acc[costCenter] = (acc[costCenter] || 0) + account.value
      return acc
    }, {})

  // Ordenar por valor
  const sortedExpenses = Object.entries(expensesByCostCenter)
    .sort(([, a], [, b]) => (b as number) - (a as number))

  return {
    labels: sortedExpenses.map(([label]) => label),
    datasets: [{
      data: sortedExpenses.map(([, value]) => value),
      backgroundColor: chartColors.value.slice(0, sortedExpenses.length)
    }]
  }
})
</script>
